// 1. React and core hooks
import { useMemo } from 'react';

// 2. External libraries
import { useClerk } from '@clerk/clerk-react';
import { getRouteApi } from '@tanstack/react-router';

// 3. Internal components
import { CenteredAuthLayout } from '~/components/common/CenteredAuthLayout';
import { Alert } from '~/components/ui/alert';
import { Button } from '~/components/ui/button';

// 4. Type imports
import type { AuthErrorScenario } from '~/types/authError';

// 5. Custom hooks and utilities
import { REGISTRATION_CLOSED_MESSAGE } from '~/utils/clerkErrorHandler';

interface ErrorScenarioConfig {
  title: string;
  message: string;
  primaryAction: {
    label: string;
    onClick: () => void;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
}

/**
 * Display user-friendly error page for authentication failures.
 *
 * Reads the validated 'type' search param and maps scenario to title, message,
 * and action buttons. Centered layout with max-width 500px.
 *
 * Per §TS.3.1 - function declaration for component
 * Per spec lines 141-147 - scenario UI mapping table
 * Reference: MagicLinkCallbackPage.tsx:174-194 (error state layout)
 */
const routeApi = getRouteApi('/auth-error');

export function AuthErrorPage() {
  const { type } = routeApi.useSearch();
  const { signOut } = useClerk();

  // Read 'type' search param, default to 'unknown' if missing or invalid
  const errorType = (type as AuthErrorScenario | undefined) ?? 'unknown';

  const handleBackToSignIn = () => {
    void signOut({ redirectUrl: '/sign-in' });
  };

  const handleContactSupport = () => {
    // TODO: Implement support contact flow
    // Placeholder for task 2.1 - actual implementation in future task
    window.location.href = 'mailto:support@ddrp.sh';
  };
  // Map scenario to config per spec lines 141-147 (§TS.13.1 - memoized mapping)
  // Per §TS.13.1 - callback stability pattern
  const scenarioConfig = useMemo((): ErrorScenarioConfig => {
    switch (errorType) {
      case 'account-suspended':
        return {
          title: 'Account Access Restricted',
          message:
            'Your account access has been restricted. Please contact support for assistance.',
          primaryAction: {
            label: 'Back to Sign In',
            onClick: handleBackToSignIn,
          },
          secondaryAction: {
            label: 'Contact Support',
            onClick: handleContactSupport,
          },
        };

      case 'trial-expired':
        // TODO(product-copy): functional placeholder text pending product/marketing copy.
        // Mirrors the account-suspended pattern: titled message plus Back to Sign In.
        // No upgrade link is offered here: an expired user hitting an in-app plan
        // page would be re-gated by the same 403, so this terminal screen only
        // exits to sign-in / support.
        return {
          title: 'Trial Period Ended',
          message:
            'Your trial period has ended. Please contact support to continue using your account.',
          primaryAction: {
            label: 'Back to Sign In',
            onClick: handleBackToSignIn,
          },
          secondaryAction: {
            label: 'Contact Support',
            onClick: handleContactSupport,
          },
        };

      case 'registration-closed':
        return {
          title: 'Account Not Found',
          message: REGISTRATION_CLOSED_MESSAGE,
          primaryAction: {
            label: 'Back to Sign In',
            onClick: handleBackToSignIn,
          },
        };

      case 'unknown':
      default:
        // A single clear action: both buttons previously called signOut, so
        // there is no distinct "Try Again" action to offer.
        return {
          title: 'Authentication Failed',
          message:
            'We encountered an unexpected error during sign-in. Please try again or contact support if the problem persists.',
          primaryAction: {
            label: 'Back to Sign In',
            onClick: handleBackToSignIn,
          },
        };
    }
    // Per §TS.13.1 - handleBackToSignIn uses signOut which is stable from useClerk
    // oxlint-disable-next-line react/exhaustive-deps
  }, [errorType]);

  // Per MagicLinkCallbackPage.tsx:174-194 - centered layout with max-width 500px
  return (
    <CenteredAuthLayout
      data-testid="auth-error-container"
      contentWidth="wide"
      contentProps={{ 'data-testid': 'auth-error-content' }}
    >
      {/* Per spec line 137 - error alert with semantic heading title */}
      <Alert variant="destructive" role="alert" className="mb-6">
        <div className="col-start-2">
          {/* Render title as a semantic heading so assistive tech and role-based
                queries (§FE.11.1) can target it. */}
          <h2 className="mb-1 font-medium tracking-tight">{scenarioConfig.title}</h2>
          {scenarioConfig.message}
        </div>
      </Alert>

      {/* Action buttons stacked vertically */}
      <div className="flex flex-col gap-4">
        <Button
          variant="default"
          size="lg"
          className="w-full"
          onClick={scenarioConfig.primaryAction.onClick}
        >
          {scenarioConfig.primaryAction.label}
        </Button>

        {scenarioConfig.secondaryAction && (
          <Button
            variant="outline"
            size="lg"
            className="w-full"
            onClick={scenarioConfig.secondaryAction.onClick}
          >
            {scenarioConfig.secondaryAction.label}
          </Button>
        )}
      </div>
    </CenteredAuthLayout>
  );
}
